import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import { CREATE_USER_BOOK_BOOKMARK } from "arena/queries";

/**
 * Диалог для создания новой закладки
 */
export default function AddBookmarkDialog({
  open,
  onClose,
  contentId,
  bookId,
  currentCharacter,
  currentChapterName,
  progressPercent,
  onBookmarkCreated,
}) {
  const { t } = useTranslation("arena");
  const { enqueueSnackbar } = useSnackbar();
  const [bookmarkName, setBookmarkName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const [createBookmark] = useMutation(CREATE_USER_BOOK_BOOKMARK, {
    onCompleted: (data) => {
      setIsCreating(false);
      enqueueSnackbar(t("bookReader.addBookmark.success", "Закладка создана"), {
        variant: "success",
      });
      onBookmarkCreated?.(data.createUserBookBookmark);
      handleClose();
    },
    onError: (error) => {
      setIsCreating(false);
      console.error("Ошибка создания закладки:", error);
      enqueueSnackbar(
        t("bookReader.addBookmark.error", "Ошибка создания закладки"),
        { variant: "error" }
      );
    },
  });

  const handleClose = () => {
    setBookmarkName("");
    setIsCreating(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!bookmarkName.trim()) {
      enqueueSnackbar(
        t("bookReader.addBookmark.nameRequired", "Введите название закладки"),
        { variant: "warning" }
      );
      return;
    }

    setIsCreating(true);

    try {
      await createBookmark({
        variables: {
          input: {
            contentId,
            bookId,
            character: currentCharacter,
            note: bookmarkName.trim(),
            chapterName: currentChapterName,
          },
        },
      });
    } catch (error) {
      console.error("Ошибка при создании закладки:", error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle>
        {t("bookReader.addBookmark.title", "Добавить закладку")}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {currentChapterName && (
              <>
                <strong>{t("bookReader.addBookmark.chapter", "Глава")}:</strong>{" "}
                {currentChapterName}
                <br />
              </>
            )}
            <strong>
              {t("bookReader.addBookmark.progress", "Прогресс")}:
            </strong>{" "}
            {progressPercent?.toFixed(1)}%
          </Typography>
        </Box>

        <TextField
          autoFocus
          margin="dense"
          label={t("bookReader.addBookmark.nameLabel", "Название закладки")}
          type="text"
          fullWidth
          variant="outlined"
          value={bookmarkName}
          onChange={(e) => setBookmarkName(e.target.value)}
          placeholder={t(
            "bookReader.addBookmark.namePlaceholder",
            "Например: Важная мысль"
          )}
          disabled={isCreating}
          inputProps={{
            maxLength: 100,
          }}
          helperText={`${bookmarkName.length}/100`}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>
          {t("common.cancel", "Отмена")}
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isCreating || !bookmarkName.trim()}
        >
          {isCreating
            ? t("bookReader.addBookmark.creating", "Создаём...")
            : t("bookReader.addBookmark.create", "Создать")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddBookmarkDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contentId: PropTypes.string.isRequired,
  bookId: PropTypes.string.isRequired,
  currentCharacter: PropTypes.number.isRequired,
  currentChapterName: PropTypes.string,
  progressPercent: PropTypes.number,
  onBookmarkCreated: PropTypes.func,
};
